/**
 * Cryptography utilities for encrypting and decrypting API keys
 * This implementation uses AES-GCM for secure encryption with a key derived from the user's ID
 */

/**
 * Generate an encryption key from the user ID
 * In a production environment, this should use a more secure approach with a server-side secret
 * 
 * @param userId The unique user ID
 * @returns A CryptoKey object for encryption/decryption
 */
export async function generateEncryptionKey(userId: string): Promise<CryptoKey> {
  // Use the user ID as seed for the key derivation
  // In production, this should be combined with a server-side secret
  const encoder = new TextEncoder();
  const keyMaterial = await crypto.subtle.digest(
    'SHA-256',
    encoder.encode(userId)
  );
  
  // Import the key material
  return crypto.subtle.importKey(
    'raw',
    keyMaterial,
    { name: 'AES-GCM' },
    false,  // Not extractable
    ['encrypt', 'decrypt']
  );
}

/**
 * Encrypt an API key
 * 
 * @param userId The user's ID
 * @param apiKey The API key to encrypt
 * @returns The encrypted API key as a base64 string with IV
 */
export async function encryptApiKey(userId: string, apiKey: string): Promise<string> {
  const key = await generateEncryptionKey(userId);
  const encoder = new TextEncoder();
  const iv = crypto.getRandomValues(new Uint8Array(12)); // 12 bytes IV for AES-GCM
  
  // Encrypt the API key
  const encryptedData = await crypto.subtle.encrypt(
    { 
      name: 'AES-GCM', 
      iv 
    },
    key,
    encoder.encode(apiKey)
  );
  
  // Combine the IV and encrypted data
  const combined = new Uint8Array(iv.length + encryptedData.byteLength);
  combined.set(iv);
  combined.set(new Uint8Array(encryptedData), iv.length);
  
  // Convert to base64 for storage
  return btoa(String.fromCharCode(...combined));
}

/**
 * Decrypt an API key
 * 
 * @param userId The user's ID
 * @param encryptedApiKey The encrypted API key as a base64 string with IV
 * @returns The decrypted API key
 */
export async function decryptApiKey(userId: string, encryptedApiKey: string): Promise<string> {
  try {
    const key = await generateEncryptionKey(userId);
    
    // Convert from base64 and separate IV and encrypted data
    const combined = new Uint8Array(
      atob(encryptedApiKey).split('').map(c => c.charCodeAt(0))
    );
    
    const iv = combined.slice(0, 12);
    const encryptedData = combined.slice(12);
    
    // Decrypt the API key
    const decryptedData = await crypto.subtle.decrypt(
      { 
        name: 'AES-GCM', 
        iv 
      },
      key,
      encryptedData
    );
    
    // Convert back to string
    const decoder = new TextDecoder();
    return decoder.decode(decryptedData);
  } catch (error) {
    console.error('Failed to decrypt API key:', error);
    return '';
  }
} 